import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Receipt, Trophy, Clock, CheckCircle, XCircle, MinusCircle,
    TrendingUp, TrendingDown, Calendar, ChevronDown, ChevronRight,
    Banknote, Target, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BetHistoryPage() {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [bets, setBets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedBet, setExpandedBet] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login?redirect=/bets');
            return;
        }
        fetchBetHistory();
    }, [isAuthenticated, navigate]);

    const fetchBetHistory = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/bets/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBets(res.data.bets || []);
        } catch (err) {
            console.error('Failed to fetch bet history:', err);
            toast.error('Failed to load bet history');
        } finally {
            setLoading(false);
        }
    };

    // Calculate summary stats
    const stats = {
        totalBets: bets.length,
        totalStaked: bets.reduce((sum, b) => sum + (b.total_stake || 0), 0),
        totalReturns: bets.filter(b => b.status === 'settled').reduce((sum, b) => sum + (b.total_returns || 0), 0),
        pendingBets: bets.filter(b => b.status === 'placed').length,
        settledBets: bets.filter(b => b.status === 'settled').length,
        profit: bets.filter(b => b.status === 'settled').reduce((sum, b) => sum + (b.profit || 0), 0)
    };

    const getOutcomeIcon = (outcome) => {
        switch (outcome) {
            case 'won':
                return <Trophy className="h-4 w-4 text-amber-400" />;
            case 'placed':
                return <CheckCircle className="h-4 w-4 text-blue-400" />;
            case 'lost':
                return <XCircle className="h-4 w-4 text-red-400" />;
            case 'void':
                return <MinusCircle className="h-4 w-4 text-gray-400" />;
            default:
                return <Clock className="h-4 w-4 text-muted-foreground" />;
        }
    };

    const getOutcomeBadge = (outcome) => {
        switch (outcome) {
            case 'won':
                return <Badge className="bg-amber-500/20 text-amber-400">Winner</Badge>;
            case 'placed':
                return <Badge className="bg-blue-500/20 text-blue-400">Placed</Badge>;
            case 'lost':
                return <Badge className="bg-red-500/20 text-red-400">Lost</Badge>;
            case 'void':
                return <Badge className="bg-gray-500/20 text-gray-400">Void</Badge>;
            default:
                return <Badge variant="outline">Pending</Badge>;
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'settled':
                return <Badge className="bg-green-500/20 text-green-400">Settled</Badge>;
            case 'placed':
                return <Badge className="bg-blue-500/20 text-blue-400">Running</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    if (loading) {
        return (
            <div className="container-app py-8">
                <Skeleton className="h-10 w-1/3 mb-4" />
                <div className="grid md:grid-cols-4 gap-4 mb-8">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-96" />
            </div>
        );
    }

    return (
        <div className="container-app py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-heading font-bold flex items-center gap-3 page-title">
                        <Receipt className="h-8 w-8 text-primary" />
                        Bet History
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Track your betting performance (Demo Mode)
                    </p>
                </div>
                <Button variant="outline" onClick={fetchBetHistory}>
                    Refresh
                </Button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/20 rounded-lg">
                                <Receipt className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-data">{stats.totalBets}</p>
                                <p className="text-sm text-muted-foreground">Total Bets</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-500/20 rounded-lg">
                                <Banknote className="h-5 w-5 text-amber-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-data">£{stats.totalStaked.toFixed(2)}</p>
                                <p className="text-sm text-muted-foreground">Total Staked</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-500/20 rounded-lg">
                                <TrendingUp className="h-5 w-5 text-green-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold font-data">£{stats.totalReturns.toFixed(2)}</p>
                                <p className="text-sm text-muted-foreground">Total Returns</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card className={stats.profit >= 0 ? 'border-green-500/30' : 'border-red-500/30'}>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${stats.profit >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                                {stats.profit >= 0 ? (
                                    <TrendingUp className="h-5 w-5 text-green-400" />
                                ) : (
                                    <TrendingDown className="h-5 w-5 text-red-400" />
                                )}
                            </div>
                            <div>
                                <p className={`text-2xl font-bold font-data ${stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {stats.profit >= 0 ? '+' : ''}£{stats.profit.toFixed(2)}
                                </p>
                                <p className="text-sm text-muted-foreground">Profit/Loss</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pending/Running indicator */}
            {stats.pendingBets > 0 && (
                <Card className="mb-6 border-blue-500/30 bg-blue-500/5">
                    <CardContent className="py-3">
                        <div className="flex items-center gap-3">
                            <Clock className="h-5 w-5 text-blue-400 animate-pulse" />
                            <p className="text-sm">
                                <span className="font-semibold text-blue-400">{stats.pendingBets}</span> bet{stats.pendingBets > 1 ? 's' : ''} still running - 
                                results will update automatically when races finish
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Bet List */}
            {bets.length === 0 ? (
                <Card>
                    <CardContent className="py-16 text-center">
                        <Receipt className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">No Bets Yet</h3>
                        <p className="text-muted-foreground mb-4">
                            You haven&apos;t placed any bets yet. Head to the races to get started!
                        </p>
                        <Button onClick={() => navigate('/races')}>
                            View Races
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {bets.map((bet) => (
                        <Card 
                            key={bet.id}
                            className={`overflow-hidden transition-colors ${
                                bet.status === 'settled' && bet.profit > 0 ? 'border-green-500/30' :
                                bet.status === 'settled' && bet.profit < 0 ? 'border-red-500/30' :
                                ''
                            }`}
                        >
                            <Collapsible 
                                open={expandedBet === bet.id}
                                onOpenChange={() => setExpandedBet(expandedBet === bet.id ? null : bet.id)}
                            >
                                <CollapsibleTrigger asChild>
                                    <div className="p-4 cursor-pointer hover:bg-muted/30 transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className={`p-2 rounded-lg ${
                                                    bet.status === 'settled' && bet.profit > 0 ? 'bg-green-500/20' :
                                                    bet.status === 'settled' && bet.profit < 0 ? 'bg-red-500/20' :
                                                    'bg-muted'
                                                }`}>
                                                    {bet.status === 'settled' && bet.profit > 0 ? (
                                                        <Trophy className="h-5 w-5 text-green-400" />
                                                    ) : bet.status === 'settled' && bet.profit < 0 ? (
                                                        <XCircle className="h-5 w-5 text-red-400" />
                                                    ) : (
                                                        <Clock className="h-5 w-5 text-muted-foreground" />
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-semibold capitalize">
                                                            {bet.bet_type === 'singles' ? `${bet.bet_count || 1} Single${(bet.bet_count || 1) > 1 ? 's' : ''}` : 
                                                             bet.bet_type === 'accumulator' ? `${bet.fold}-Fold Acca` : bet.bet_type}
                                                        </span>
                                                        {getStatusBadge(bet.status)}
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <Calendar className="h-3 w-3" />
                                                            {new Date(bet.placed_at).toLocaleDateString('en-GB', { 
                                                                day: 'numeric', 
                                                                month: 'short',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                        <span>Stake: £{bet.total_stake?.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center gap-4">
                                                <div className="text-right">
                                                    {bet.status === 'settled' ? (
                                                        <>
                                                            <p className="text-sm text-muted-foreground">Returns</p>
                                                            <p className={`font-bold font-data text-lg ${
                                                                bet.profit > 0 ? 'text-green-400' : 
                                                                bet.profit < 0 ? 'text-red-400' : ''
                                                            }`}>
                                                                £{bet.total_returns?.toFixed(2) || '0.00'}
                                                            </p>
                                                            <p className={`text-xs ${bet.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                                {bet.profit >= 0 ? '+' : ''}£{bet.profit?.toFixed(2)}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <p className="text-sm text-muted-foreground">Potential</p>
                                                            <p className="font-bold font-data text-lg">
                                                                £{bet.potential_return?.toFixed(2) || '0.00'}
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                                <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${
                                                    expandedBet === bet.id ? 'rotate-180' : ''
                                                }`} />
                                            </div>
                                        </div>
                                    </div>
                                </CollapsibleTrigger>
                                
                                <CollapsibleContent>
                                    <div className="px-4 pb-4 pt-0 border-t bg-muted/10">
                                        <h4 className="font-semibold text-sm py-3 flex items-center gap-2">
                                            <Target className="h-4 w-4 text-primary" />
                                            Selections
                                        </h4>
                                        
                                        {bet.bet_type === 'singles' && bet.bets?.map((single, idx) => (
                                            <div 
                                                key={idx}
                                                className={`p-3 rounded-lg mb-2 ${
                                                    single.outcome === 'won' ? 'bg-amber-500/10 border border-amber-500/30' :
                                                    single.outcome === 'placed' ? 'bg-blue-500/10 border border-blue-500/30' :
                                                    single.outcome === 'lost' ? 'bg-red-500/10 border border-red-500/30' :
                                                    single.outcome === 'void' ? 'bg-gray-500/10 border border-gray-500/30' :
                                                    'bg-muted/30'
                                                }`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-start gap-3">
                                                        {getOutcomeIcon(single.outcome)}
                                                        <div>
                                                            <p className="font-semibold">{single.selection?.entrant_name}</p>
                                                            <p className="text-sm text-muted-foreground">{single.selection?.event_title}</p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                {single.selection?.meeting_name} • {single.selection?.race_time ? 
                                                                    new Date(single.selection.race_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="flex items-center gap-2 justify-end mb-1">
                                                            <span className="font-data">{single.selection?.odds?.toFixed(2) || '-'}</span>
                                                            {single.each_way && <Badge variant="outline" className="text-xs">E/W</Badge>}
                                                        </div>
                                                        <p className="text-sm">Stake: £{single.stake?.toFixed(2)}</p>
                                                        {single.settled && (
                                                            <div className="mt-2">
                                                                {getOutcomeBadge(single.outcome)}
                                                                {single.result_position && (
                                                                    <p className="text-xs text-muted-foreground mt-1">
                                                                        Finished {single.result_position === 1 ? '1st' : 
                                                                                 single.result_position === 2 ? '2nd' : 
                                                                                 single.result_position === 3 ? '3rd' : 
                                                                                 `${single.result_position}th`}
                                                                    </p>
                                                                )}
                                                                <p className={`text-sm font-data font-semibold mt-1 ${
                                                                    single.returns > single.stake ? 'text-green-400' :
                                                                    single.returns === single.stake ? 'text-muted-foreground' :
                                                                    'text-red-400'
                                                                }`}>
                                                                    Return: £{single.returns?.toFixed(2) || '0.00'}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {!single.settled && (
                                                            <Badge variant="outline" className="mt-2">Pending</Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        
                                        {bet.bet_type === 'accumulator' && (
                                            <div className="p-3 rounded-lg bg-muted/30">
                                                <p className="text-sm text-muted-foreground mb-2">
                                                    {bet.fold}-Fold Accumulator @ {bet.combined_odds?.toFixed(2)}
                                                    {bet.each_way && ' (Each-Way)'}
                                                </p>
                                                <div className="space-y-2">
                                                    {bet.selections?.map((sel, idx) => (
                                                        <div key={idx} className="flex items-center justify-between text-sm">
                                                            <span>{sel.entrant_name}</span>
                                                            <span className="font-data">{sel.odds?.toFixed(2)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Summary */}
                                        <div className="mt-4 pt-4 border-t flex items-center justify-between">
                                            <div className="text-sm text-muted-foreground">
                                                Bet ID: {bet.id?.slice(0, 8)}...
                                            </div>
                                            <div className="text-right">
                                                <span className="text-sm text-muted-foreground mr-4">
                                                    Total Stake: <strong>£{bet.total_stake?.toFixed(2)}</strong>
                                                </span>
                                                {bet.status === 'settled' ? (
                                                    <span className={`font-semibold ${bet.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                        {bet.profit >= 0 ? 'Won' : 'Lost'}: {bet.profit >= 0 ? '+' : ''}£{bet.profit?.toFixed(2)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">
                                                        Potential: £{bet.potential_return?.toFixed(2)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        </Card>
                    ))}
                </div>
            )}

            {/* Disclaimer */}
            <Card className="mt-8 border-amber-500/30 bg-amber-500/5">
                <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold text-amber-400">Demo Mode</p>
                            <p className="text-sm text-muted-foreground">
                                This is a demonstration feature. No real money is involved. 
                                Bet responsibly and only gamble what you can afford to lose.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
